import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { banco } from '../../db/banco'
import { novoRegistroBase } from '../../db/tipos'
import type { Meta } from '../../db/tipos'
import { gravarLocalEEnfileirar } from '../../sync/outbox'
import { ROTULOS_TIPO_META } from '../../metas/rotulos'
import { mesDeOndeRepetir, metasParaRepetir } from '../../metas/repetir'
import { primeiroDiaDoMes, ultimoDiaDoMes } from '../../metas/destrave'
import { lerCampoAlvo } from '../../metas/alvos'
import { useSalvamento } from '../../componentes/useSalvamento'
import BotaoSalvar from '../../componentes/BotaoSalvar'
import FormMetaNova from './FormMetaNova'

const CAMPO = 'w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm'

interface Linha {
  chave: string
  escopo: string
  tipo: Meta['tipo']
  valor: string
}

function mesCorrente(): string {
  const hoje = new Date()
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`
}

function mesAnteriorA(mes: string): string {
  const [ano, m] = mes.split('-').map(Number)
  const d = new Date(ano, m - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export default function RepetirMetas() {
  const navegar = useNavigate()
  const mesAlvo = mesCorrente()
  const [origem, setOrigem] = useState<string | null | undefined>(undefined)
  const [linhas, setLinhas] = useState<Linha[]>([])
  const [acrescentando, setAcrescentando] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  // Enquanto ela nao mexeu, a tela segue o banco. Depois que mexeu, para de
  // seguir -- senao uma sincronizacao chegando desfaria o que ela apagou ou
  // acrescentou. Mesma flag do criador e da configuracao.
  const [tocado, setTocado] = useState(false)
  const { estado, executar, reiniciar } = useSalvamento()

  const representadas = useLiveQuery(
    () => banco.representadas.filter((r) => r.apagado_em === null).sortBy('nome'), [])

  // ⚠ As metas do mes passado vem da SINCRONIZACAO. Num aparelho recem aberto
  // elas chegam depois desta tela montar, e sem observar isso a tela diria
  // "Nao ha metas em nenhum mes anterior para repetir" para quem tem metas.
  // Terceira tela deste ciclo com o mesmo risco (configuracao, criador, esta).
  const quantasMetas = useLiveQuery(() => banco.metas.count(), [])

  useEffect(() => {
    if (tocado) return
    let atual = true
    void (async () => {
      const mes = await mesDeOndeRepetir(mesAlvo)
      if (!atual) return
      setOrigem(mes)
      if (!mes) {
        setLinhas([])
        return
      }
      const metas = await metasParaRepetir(mes)
      if (!atual) return
      setLinhas(metas.map((m) => ({
        chave: m.id, escopo: m.escopo, tipo: m.tipo, valor: String(m.valor_alvo),
      })))
    })()
    return () => {
      atual = false
    }
  }, [mesAlvo, quantasMetas, tocado])

  function nomeEscopo(valorEscopo: string): string {
    if (valorEscopo === 'pessoal') return 'Pessoal'
    const representada = representadas?.find(
      (r) => r.id === valorEscopo.slice('representada:'.length),
    )
    return representada?.nome ?? 'Fornecedora'
  }

  function mexer(alteracao: () => void) {
    reiniciar()
    setAviso(null)
    setTocado(true)
    alteracao()
  }

  async function confirmar() {
    const paraGravar: Array<{ linha: Linha; valor: number }> = []
    const ruins: string[] = []
    for (const linha of linhas) {
      const leitura = lerCampoAlvo(linha.valor)
      // Vazio aqui tambem e intencao: e um jeito de tirar a meta sem apagar a
      // linha. Ilegivel e erro, e vira aviso em vez de sumir calado.
      if (leitura.estado === 'vazio') continue
      if (leitura.estado === 'invalido') {
        ruins.push(`${ROTULOS_TIPO_META[linha.tipo]} (${nomeEscopo(linha.escopo)})`)
        continue
      }
      paraGravar.push({ linha, valor: leitura.valor })
    }

    if (ruins.length > 0) {
      setAviso(`Confira o valor de: ${ruins.join(', ')}.`)
      return
    }
    if (paraGravar.length === 0) {
      setAviso('Preencha ao menos uma meta para repetir.')
      return
    }
    setAviso(null)

    await executar(async () => {
      for (const { linha, valor } of paraGravar) {
        await gravarLocalEEnfileirar('metas', {
          ...novoRegistroBase(),
          escopo: linha.escopo,
          tipo: linha.tipo,
          periodo_inicio: primeiroDiaDoMes(mesAlvo),
          periodo_fim: ultimoDiaDoMes(mesAlvo),
          valor_alvo: valor,
        })
      }
      // Dentro do executar: gravacao que falha nao pode sair da tela em silencio.
      navegar('/metas')
    })
  }

  if (origem === undefined) {
    return <p className="p-4 text-sm text-slate-500">Carregando…</p>
  }

  if (origem === null) {
    return (
      <section className="p-4 space-y-4">
        <h2 className="text-lg font-bold text-slate-800">Repetir metas</h2>
        <p className="text-sm text-slate-600">
          Não há metas em nenhum mês anterior para repetir.
        </p>
        <button
          onClick={() => navegar('/metas')}
          className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
        >
          Voltar
        </button>
      </section>
    )
  }

  const anterior = mesAnteriorA(mesAlvo)

  return (
    <section className="p-4 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Repetir metas — {mesAlvo}</h2>

      {/* Dizer de qual mes veio: sem isto ela seria pega de surpresa por
          numeros de dois meses atras. */}
      <p data-testid="origem-repeticao" className="text-sm text-slate-600">
        {anterior !== origem
          ? `${anterior} não tem metas. Repetindo as de ${origem}:`
          : `Repetindo as metas de ${origem}:`}
      </p>

      <div className="bg-white rounded-xl shadow-sm divide-y">
        {linhas.map((linha) => (
          <div key={linha.chave} className="p-3 flex items-end gap-2">
            <div className="flex-1 min-w-0">
              {/* ⚠ O escopo entra no rotulo: a mesma meta pessoal e por marca
                  convivem no mesmo mes, e so o tipo faria DUAS linhas iguais na
                  tela, sem ela saber qual e qual. */}
              <span className="block text-xs text-slate-500 mb-0.5 truncate">
                {ROTULOS_TIPO_META[linha.tipo]} — {nomeEscopo(linha.escopo)}
              </span>
              <input
                data-testid={`repetir-${linha.tipo}`} className={CAMPO} value={linha.valor}
                onChange={(e) => mexer(() => setLinhas((atuais) => atuais.map((l) =>
                  l.chave === linha.chave ? { ...l, valor: e.target.value } : l)))}
              />
            </div>
            <button
              aria-label={`Tirar ${ROTULOS_TIPO_META[linha.tipo]} de ${nomeEscopo(linha.escopo)}`}
              onClick={() => mexer(() =>
                setLinhas((atuais) => atuais.filter((l) => l.chave !== linha.chave)))}
              className="text-red-600 text-sm px-2 py-1.5"
            >
              ×
            </button>
          </div>
        ))}
        {linhas.length === 0 && (
          <p className="p-3 text-sm text-slate-500">Você tirou todas as metas.</p>
        )}
      </div>

      <button
        data-testid="acrescentar-meta" type="button"
        onClick={() => setAcrescentando(!acrescentando)}
        className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 text-sm"
      >
        {acrescentando ? 'Fechar' : '+ acrescentar meta'}
      </button>

      {acrescentando && (
        <FormMetaNova
          aoIncluir={(nova) => {
            // ⚠ Nada e gravado ao incluir: a linha so entra na lista local. A
            // gravacao continua sendo so no Confirmar, senao "acrescentar e
            // desistir" deixaria lixo no banco.
            mexer(() => setLinhas((atuais) => [...atuais, {
              chave: `nova-${atuais.length}-${Date.now()}`,
              escopo: nova.escopo, tipo: nova.tipo, valor: String(nova.valorAlvo),
            }]))
            setAcrescentando(false)
          }}
        />
      )}

      {aviso && <p data-testid="aviso-repetir" className="text-sm text-amber-700">{aviso}</p>}

      {linhas.length > 0 && (
        <div data-testid="confirmar-repeticao">
          <BotaoSalvar estado={estado} type="button" onClick={() => void confirmar()}>
            Confirmar
          </BotaoSalvar>
        </div>
      )}

      <button
        onClick={() => navegar('/metas')}
        className="w-full border border-teal-700 text-teal-700 rounded-lg py-2 font-medium"
      >
        Voltar
      </button>
    </section>
  )
}
